/**
 * tutorial/steps.js — the onboarding script (GDD §4, Tutorial row).
 *
 * Content only: the words, the thing each step points at, and the real game
 * event that counts as "done". No rendering and no state — `tutorial/index.js`
 * owns both. Kept out of `constants.js` on purpose: this is copy, not tuning
 * data, and it changes for editorial reasons rather than balance ones.
 *
 * Every step advances on something the player genuinely did. There is no
 * "click next to continue" anywhere, because a first-time VR player who is
 * being told to throw a ball should advance by throwing a ball.
 *
 * @module tutorial/steps
 */

import { EVENTS } from '../constants.js';

/** The stall onboarding walks you through. The GDD's exemplar. */
export const TUTORIAL_STALL = 'tin-can-alley';

/**
 * @typedef {Object} TutorialStep
 * @property {string} id
 * @property {string} title
 * @property {string} body
 * @property {string} [hint] Control hint. May contain `<kbd>` markup.
 * @property {'spawn'|'stall'|'prize-desk'|'portal'|null} marker
 *   Which world anchor the floating arrow sits over. Null hides it.
 * @property {string} [event] Bus event that completes the step.
 * @property {(payload: object) => boolean} [when] Extra guard on that event.
 * @property {'moved'} [gesture] Non-event completion, polled in update().
 * @property {number} [timeout] Seconds after which the step self-completes.
 *   Onboarding must never be a dead end, so the last step always has one.
 */

/** @type {TutorialStep[]} */
export const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to CARNIVAL CHAOS!',
    body: "You're a goose. Honk.",
    hint: 'Waddle with <kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd>, or press <kbd>T</kbd> to teleport.',
    marker: null,
    gesture: 'moved',
    timeout: 30,
  },
  {
    id: 'find-stall',
    title: 'Follow the arrow',
    body: 'Tin Can Alley is down the midway. Waddle up to the counter.',
    hint: 'The glowing ring is where you stand.',
    marker: 'stall',
    event: EVENTS.STALL_APPROACH,
    when: (p) => p.stallId === TUTORIAL_STALL,
  },
  {
    id: 'insert-ticket',
    title: 'Pay the barker',
    body: 'Tickets buy a round. You start with 500 — the count is up in the corner.',
    hint: 'Press <kbd>F</kbd> to play. In VR, pull the trigger.',
    marker: 'stall',
    event: EVENTS.STALL_ACTIVATE,
    when: (p) => p.stallId === TUTORIAL_STALL,
  },
  {
    id: 'throw',
    title: 'Knock the cans down',
    body: 'Grab a ball off the tray and let it fly.',
    hint: 'Hold <kbd>E</kbd> or the left mouse button, flick the mouse, release. In VR, squeeze the grip.',
    marker: null,
    event: EVENTS.THROW,
  },
  {
    id: 'payout',
    title: 'That is your payout',
    body: 'Score becomes redemption tokens. Tokens buy prizes — tickets only buy rounds.',
    hint: 'Play again, or press <kbd>Esc</kbd> to step away.',
    marker: null,
    event: EVENTS.STALL_ROUND_END,
    timeout: 25,
  },
  {
    id: 'spend',
    title: 'Spend them at the far end',
    body: 'The Prize Desk trades tokens for plushes. The purple portal beside it is your own space — that is where your fish live.',
    hint: 'Go and have a look whenever you like.',
    marker: 'prize-desk',
    timeout: 22,
  },
];

/** @type {Record<string, TutorialStep>} */
export const STEP_BY_ID = Object.freeze(
  Object.fromEntries(TUTORIAL_STEPS.map((s) => [s.id, s])),
);
